import type { GetHistoryResponse, HistoryActor, HistoryChanges, HistoryEntry, HistoryValue } from '@seiri/shared/common'

/**
 * Slack for a stamp written in the same save as the subject's first audit row: the row's `at` and the stamp come from
 * two clocks a few milliseconds apart, and the stamp must not show twice.
 */
const SAME_SAVE_MS = 5_000

/**
 * Whether a História card still shows a stamp that keeps only the latest value (`updatedAt`, `cancelledAt`,
 * `waiting`, `addedToSeriesAt`) - docs/audit.md, _Merging with the stamps_.
 *
 * From a subject's first audit row on, the log is the authority for it: a stamp from before that moment is the only
 * record of what it says and is shown, a later one is already in the log and is not. A subject with no audit row,
 * everything before the log existed, keeps its stamps.
 */
export function legacyStampShown(
  at: Date | string | null | undefined,
  subjectId: string,
  history: GetHistoryResponse,
): boolean {
  if (!at) {
    return false
  }
  const since = history.auditedSince[subjectId]
  return !since || new Date(at).getTime() < new Date(since).getTime() - SAME_SAVE_MS
}

/** The entries about one subject - a sign-up's own, out of its event's history. */
export function historyOf(history: GetHistoryResponse, subjectId: string): HistoryEntry[] {
  return history.entries.filter((entry) => entry.subjectId === subjectId)
}

/**
 * Who made a change, for the detail line of a História item (docs/wording.md, _História_): a member of staff by name,
 * a client as "klient", a public form as "cez web". The system, and a person no longer known, are left out.
 */
export function historyActorText(actor: HistoryActor): string {
  switch (actor.kind) {
    case 'staff':
    case 'admin':
      return actor.name ?? ''
    case 'customer':
      return 'klient'
    case 'public':
      return 'cez web'
    default:
      return ''
  }
}

/** How one field of an edit is told: its label, and how to show its values. Without `value`, only the label. */
export interface HistoryChangeField {
  label: string
  value?: (value: HistoryValue) => string
}

/** A value with nothing in it - no place, no price, no employees - shown as a dash. */
export const EMPTY_VALUE = '–'

/** A value as text: a list joined, nothing as a dash. For the fields whose value needs no formatting of its own. */
export function plainHistoryValue(value: HistoryValue): string {
  if (value === null || value === '') {
    return EMPTY_VALUE
  }
  if (Array.isArray(value)) {
    return value.length ? value.join(', ') : EMPTY_VALUE
  }
  return String(value)
}

/**
 * What an edit changed, in the order of `fields`: "kapacita 10 → 12, miesto Sála A → Sála B, popis". A field the
 * value of which is not kept (a description), or not worth showing, is named only.
 */
export function historyChangesText<F extends string>(
  changes: HistoryChanges<F>,
  fields: Record<F, HistoryChangeField>,
): string {
  return (Object.keys(fields) as F[])
    .flatMap((field) => {
      if (!(field in changes)) {
        return []
      }
      const change = changes[field]
      const { label, value } = fields[field]
      return change && value ? [`${label} ${value(change.from)} → ${value(change.to)}`] : [label]
    })
    .join(', ')
}
